import { animated, useSpring } from "@react-spring/three";
import { useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { forwardRef, useEffect, useRef } from "react";
import * as THREE from "three";
import { MeshStandardMaterial } from "three";
import { singleCasementOptions } from "../utils/optionsMenuData";
import useSetMaterial from "../utils/useSetMaterial";
import {
  Geometry,
  Base,
  Addition,
  Subtraction,
  ReverseSubtraction,
  Intersection,
  Difference,
} from "@react-three/csg";

const TargetCube = forwardRef(({ position }, ref) => {
  return (
    <mesh ref={ref} position={position} scale={[0, 0, 0]}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="red" />
    </mesh>
  );
});

//region SingleCasementWindow
export function SingleCasementWindow({
  position,
  windowOpen,
  activeConfig,
  activeHinge,
  activeBugscreen,
  activeBlind,
  activeBlindDirection,
  activePaintColor,
  activePaintedSide,
  activePremiumCenterLock,
  activeHardware,
  activeFrostedGlass,
  activeGrillStyle,
  activeGrillPattern,
  activeSDLStyle,
  activeSDLPattern,
  activeExtraScreenBlindCartridge,
  activeLimiter,
  activeNightLock,
}) {
  const { nodes, materials } = useGLTF("/models/RSingle Casement Window.glb");

  const { bugMeshPosition } = useSpring({
    bugMeshPosition: windowOpen ? [-0.6, 0, 0] : [0, 0, 0],
    config: { duration: 2000 },
    delay: windowOpen ? 0 : 3000,
  });

  const { innerFramePosition } = useSpring({
    innerFramePosition: windowOpen
      ? [0.145, 0.023, -0.022]
      : [-0.33, 0.023, -0.022],
    config: { duration: 2000 },
    delay: windowOpen ? 3000 : 0,
  });

  const { handleRotation } = useSpring({
    handleRotation: windowOpen ? [0, 0, (Math.PI / 180) * 90] : [0, 0, 0],
    config: { duration: 1000 },
    delay: windowOpen ? 2000 : 2000,
  });

  const { rodRotation } = useSpring({
    rodRotation: windowOpen ? [0, -(Math.PI / 180) * 61, 0] : [0, 0, 0],
    config: { duration: 2000 },
    delay: windowOpen ? 3000 : 0,
  });

  const finishHandle = new MeshStandardMaterial();

  const glassMaterial = new THREE.MeshPhysicalMaterial({
    // color: 0x000000,
    metalness: 0,
    roughness: 0,
    // transmission: 5000,
    opacity: 0.2,
    transparent: true,
    ior: 2.33,
  });

  const netMaterial = new THREE.MeshPhysicalMaterial({
    // color: 0x000000,
    metalness: 0,
    roughness: 0,
    // transmission: 1,
    opacity: 0.9,
    transparent: true,
    // ior: 2.33,
  });

  useSetMaterial(materials.body_paint, "white");

  useSetMaterial(
    materials.body_paint,
    singleCasementOptions.paintColor.find(
      (option) => option.id === activePaintColor
    ).internal
  );

  useSetMaterial(
    finishHandle,
    singleCasementOptions.paintColor.find(
      (option) => option.id === activeHardware
    ).internal
  );

  useSetMaterial(
    netMaterial,
    singleCasementOptions.blind.find((option) => option.id === activeBlind)
      .internal
  );

  // useSetMaterial(
  //   netMaterial,
  //   finishHandle,
  //   singleCasementOptions.blind.find((option) => option.id === activeBlind)
  //     .internal
  // );

  // useEffect(() => {
  //   console.log("dekho",
  //     singleCasementOptions.blind.find((option) => option.id === activeBlind)
  //       .internal
  //   );
  // }, [activeBlind]);

  useSetMaterial(glassMaterial, "acidGlass");

  // const targetPosition = [3, 3, 3];
  const targetRef = useRef();
  const followerRef = useRef();

  useFrame(() => {
    if (followerRef.current && targetRef.current) {
      // Get the target cube's world position
      const targetPosition = new THREE.Vector3();
      targetRef.current.getWorldPosition(targetPosition);

      // Make the pointing cube look at the target cube
      followerRef.current.lookAt(targetPosition);
    }
  });

  return (
    <group
      dispose={null}
      position={position}
      scale={activeHinge === 1 ? [1, 1, 1] : [-1, 1, 1]}
    >
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.frame_outer.geometry}
        material={materials.body_paint}
      >
        {/* Connector rod */}
        <animated.group position={[0.312, 0.04, -0.022]} rotation={rodRotation}>
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.connecting_rod.geometry}
            material={materials.connecting_rod}
            // material={Paint3}
          />
          <TargetCube ref={targetRef} position={[-0.3425, -0.017, 0]} />
        </animated.group>

        {activeBlind !== 1 ? (
          <animated.group position={bugMeshPosition}>
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.bugscreen_frame.geometry}
              material={materials.body_paint}
            />

            <mesh
              castShadow
              receiveShadow
              geometry={nodes.bugscreen3d.geometry}
              material={netMaterial}
            />
          </animated.group>
        ) : null}

        <animated.mesh
          castShadow
          receiveShadow
          geometry={nodes.slider.geometry}
          material={materials.slider}
          position={innerFramePosition}
        >
          <group ref={followerRef}>
            <mesh scale={[0.01, 0.01, 0.01]}>
              <boxGeometry args={[1, 1, 1]} />
              <meshStandardMaterial color="red" />
            </mesh>
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.frame_inner.geometry}
              material={materials.body_paint}
              rotation={[0, -Math.PI / 2, 0]}
            >
              <mesh
                castShadow
                receiveShadow
                geometry={nodes.frame_outer_rubber.geometry}
                material={materials.frame_outer_rubber}
                position={[0.339, 0.738, -0.01]}
              />
              <mesh
                castShadow
                receiveShadow
                geometry={nodes.connecter_rod.geometry}
                material={materials.frame_outer_rubber}
                position={[0.337, 0.738, -0.008]}
              />
              <mesh
                castShadow
                receiveShadow
                geometry={nodes.connecter_slider.geometry}
                material={materials.slider}
                position={[0.001, 0.788, 0]}
              />
              <mesh
                castShadow
                receiveShadow
                geometry={nodes.glass.geometry}
                // material={materials.glass}
                material={glassMaterial}
                position={[0.339, 0.741, -0.006]}
              />

              {/* Handles */}
              <group>
                <animated.mesh
                  castShadow
                  receiveShadow
                  geometry={nodes.handle_bottom.geometry}
                  // material={materials.body_paint}
                  material={finishHandle}
                  position={[0.647, 0.303, 0.019]}
                  // rotation={[0, -1.571, 0]}
                  rotation={handleRotation}
                />
                <animated.mesh
                  castShadow
                  receiveShadow
                  geometry={nodes.handle_top.geometry}
                  // material={materials.body_paint}
                  material={finishHandle}
                  position={[0.646, 1.173, 0.019]}
                  // rotation={[0, -1.571, 0]}
                  rotation={handleRotation}
                />
              </group>

              {/* Grill */}
              <group>
                <mesh
                  castShadow
                  receiveShadow
                  geometry={nodes.grill_1_colonial.geometry}
                  material={materials.body_paint}
                  position={[0.339, 0.741, -0.006]}
                  visible={activeGrillPattern === 3 ? true : false}
                />
                <mesh
                  castShadow
                  receiveShadow
                  geometry={nodes.grill_1_marginal.geometry}
                  material={materials.body_paint}
                  position={[0.339, 0.741, -0.006]}
                  visible={activeGrillPattern === 4 ? true : false}
                />
                <mesh
                  castShadow
                  receiveShadow
                  geometry={nodes.grill_1_single_hung.geometry}
                  material={materials.body_paint}
                  position={[0.339, 0.741, -0.006]}
                  visible={activeGrillPattern === 5 ? true : false}
                />
                <mesh
                  castShadow
                  receiveShadow
                  geometry={nodes.grill_1_victorian.geometry}
                  material={materials.body_paint}
                  position={[0.339, 0.741, -0.006]}
                  visible={activeGrillPattern === 2 ? true : false}
                />
              </group>

              {/* SDL */}
              <group>
                <mesh
                  castShadow
                  receiveShadow
                  geometry={nodes.SDL_colonial.geometry}
                  material={materials.body_paint}
                  position={[0.339, 0.741, -0.006]}
                  visible={activeSDLPattern === 3 ? true : false}
                />
                <mesh
                  castShadow
                  receiveShadow
                  geometry={nodes.SDL_marginal.geometry}
                  material={materials.body_paint}
                  position={[0.339, 0.741, -0.006]}
                  visible={activeSDLPattern === 4 ? true : false}
                />
                <mesh
                  castShadow
                  receiveShadow
                  geometry={nodes.SDL_single_hung.geometry}
                  material={materials.body_paint}
                  position={[0.339, 0.741, -0.006]}
                  visible={activeSDLPattern === 5 ? true : false}
                />
                <mesh
                  castShadow
                  receiveShadow
                  geometry={nodes.SDL_victorian.geometry}
                  material={materials.body_paint}
                  position={[0.339, 0.741, -0.006]}
                  visible={activeSDLPattern === 2 ? true : false}
                />
              </group>
            </mesh>
          </group>
        </animated.mesh>
      </mesh>
    </group>
  );
}

useGLTF.preload("/models/RSingle Casement Window.glb");
